import React, { useEffect, useMemo, useState } from "react";
import { Badge, Container, Table } from "react-bootstrap";
import "./AdminWorkflowMap.css";

const departments = [
  {
    key: "admin",
    title: "Admin",
    icon: "admin_panel_settings",
    tone: "blue",
    summary: "Master control, approvals, assignments, certificates, payouts and user access.",
    items: ["Departments & roles", "External user approval", "Requirement assignment", "Reports & tracking"],
  },
  {
    key: "hr",
    title: "HR",
    icon: "badge",
    tone: "rose",
    summary: "Employee lifecycle, attendance approval, payroll support and contractor HR review.",
    items: ["Employees", "Leave & attendance", "Onboarding/offboarding", "Payroll runs"],
  },
  {
    key: "accounts",
    title: "Accounts",
    icon: "account_balance_wallet",
    tone: "green",
    summary: "Ledgers, GST, invoices, vendor bills, payments, payroll and audit reports.",
    items: ["Party ledgers", "Invoices", "Payments", "Taxes & audit"],
  },
  {
    key: "plant",
    title: "Plant",
    icon: "precision_manufacturing",
    tone: "orange",
    summary: "Production, plant quality decision, material movement and contractor work approval.",
    items: ["Production", "Quality decision", "Contractor approval", "Safety & compliance"],
  },
  {
    key: "quality",
    title: "Quality",
    icon: "fact_check",
    tone: "teal",
    summary: "QA checks, supplier dispatch inspection, accepted/rejected quantity and quality report.",
    items: ["QA checks", "Supplier inspection", "Quality reports", "Approvals"],
  },
  {
    key: "purchase",
    title: "Purchase",
    icon: "shopping_cart_checkout",
    tone: "purple",
    summary: "Purchase requests, orders, vendor master, rate comparison and material availability.",
    items: ["Requests", "Orders", "Vendor master", "Rates"],
  },
  {
    key: "logistics",
    title: "Logistics",
    icon: "local_shipping",
    tone: "cyan",
    summary: "Dispatch planning, packing, transport, dispatch notes, tracking and returns.",
    items: ["Dispatch request", "Packing", "Transport", "Tracking"],
  },
  {
    key: "security",
    title: "Security",
    icon: "shield",
    tone: "slate",
    summary: "Guard attendance, vehicle lookup, gate logs, material inward and challan upload.",
    items: ["Gate entry", "Vehicle logs", "Attendance", "Material inward"],
  },
  {
    key: "sales",
    title: "Sales",
    icon: "trending_up",
    tone: "indigo",
    summary: "Leads, Meta leads, deals, targets, pricing approvals and sales reports.",
    items: ["Leads", "Deals", "Targets", "Pricing approval"],
  },
];

const externalFlows = [
  {
    title: "Vendor Requirement Flow",
    actor: "Vendor",
    steps: [
      "Register & complete profile",
      "Admin approval",
      "Requirement submitted",
      "Admin approves / assigns employee",
      "Dispatch processing",
      "Invoice auto-created",
      "Accounts payment",
    ],
  },
  {
    title: "Supplier Quality Flow",
    actor: "Supplier",
    steps: [
      "Register & PO setup",
      "Dispatch submitted",
      "Admin dispatch review",
      "Plant assigns Quality",
      "Quality inspection",
      "Plant decision",
      "Accounts payable",
    ],
  },
  {
    title: "Contractor Work Flow",
    actor: "Contractor",
    steps: [
      "Register & rates setup",
      "Daily work entry",
      "Plant approval",
      "HR review",
      "Accounts review",
      "Payment processed",
      "Receipt confirmed",
    ],
  },
];

const handoffs = [
  ["Invoice create/update", "Accounts creator", "Invoice approver", "finance:invoice:approve"],
  ["Invoice approved", "Invoice approver", "Payment processor", "finance:payments:update / approve"],
  ["Vendor bill approved", "Bill approver", "Payment processor", "finance:payments:update / approve"],
  ["Payment approved", "Payment approver", "Payment release user", "finance:payments:release"],
  ["Leave request", "HR / Employee", "Leave approver", "leave:approve"],
  ["Attendance approval", "HR attendance user", "Attendance approver", "attendance:approve"],
  ["Pricing request", "Sales user", "Pricing approver", "pricing:approve"],
  ["Contractor Plant approved", "Plant manager", "Accounts + HR", "salary:process / hr:payroll:manage"],
  ["Purchase request", "Purchase requester", "PO creator", "purchase:order:create"],
  ["Dispatch request", "Logistics planning", "Packing / Dispatch", "logistics:packing:manage"],
];

const departmentConnections = [
  {
    department: "Sales",
    icon: "trending_up",
    connectedTo: [
      ["Admin", "Lead oversight, manual lead creation and reporting"],
      ["Accounts", "Pricing, discount and payment visibility after deal closure"],
      ["Dispatch / Logistics", "Customer delivery and order movement after conversion"],
      ["Reports", "Sales performance and target tracking"],
    ],
  },
  {
    department: "Accounts",
    icon: "account_balance_wallet",
    connectedTo: [
      ["Admin", "Payouts, payroll approvals and payment monitoring"],
      ["Sales", "Invoice/payment support after sales approval"],
      ["Plant", "Contractor work payment and supplier quality payable"],
      ["Quality", "Supplier quality amount and deductions"],
      ["Purchase", "Vendor bill and purchase payment processing"],
      ["HR", "Payroll, salary approval and worktime payment data"],
      ["Vendor / Supplier / Contractor", "Ledger, payables, receivables and payment history"],
    ],
  },
  {
    department: "Plant",
    icon: "precision_manufacturing",
    connectedTo: [
      ["Quality", "Supplier material inspection and quality report review"],
      ["Accounts", "Approved contractor work and supplier payable forwarding"],
      ["HR", "Contractor work payroll coordination and queries"],
      ["Inventory", "Stock consumption, material movement and inventory alerts"],
      ["Purchase", "Material availability and production requirement support"],
      ["Supplier", "Dispatch quality decision and amount agreement"],
      ["Contractor", "Daily work approval and clarification"],
    ],
  },
  {
    department: "Quality",
    icon: "fact_check",
    connectedTo: [
      ["Plant", "Quality report submission and Plant final decision"],
      ["Supplier", "Inspection result, accepted/rejected quantity and proposed amount"],
      ["Accounts", "Approved/agreed amount for supplier payment"],
      ["Admin / HR", "Escalated quality query handling"],
    ],
  },
  {
    department: "Purchase",
    icon: "shopping_cart_checkout",
    connectedTo: [
      ["Admin", "Vendor master and purchase monitoring"],
      ["Inventory", "Material availability, GRN and stock status"],
      ["Accounts", "Vendor bills and payment processing"],
      ["Plant", "Production material need and availability"],
      ["Logistics", "Incoming/outgoing material coordination"],
    ],
  },
  {
    department: "Inventory",
    icon: "inventory",
    connectedTo: [
      ["Purchase", "GRN, stock update and material availability"],
      ["Plant", "Stock consumption and material movement"],
      ["Quality", "Inspection status before accepted stock"],
      ["Logistics", "Issue/return and dispatch stock movement"],
      ["Accounts", "Inventory reports and valuation support"],
    ],
  },
  {
    department: "Logistics",
    icon: "local_shipping",
    connectedTo: [
      ["Admin", "Requirement dispatch and tracking visibility"],
      ["Vendor", "Requirement delivery and tracking updates"],
      ["Accounts", "Dispatch-based invoice/payment handoff"],
      ["Inventory", "Stock issue/return and physical movement"],
      ["Security", "Vehicle gate movement and challan support"],
      ["Purchase", "Transport coordination for material movement"],
    ],
  },
  {
    department: "HR",
    icon: "badge",
    connectedTo: [
      ["Admin", "Employee approval, department-role assignment and reports"],
      ["Accounts", "Salary, payroll, worktime and contractor payment support"],
      ["Plant", "Contractor work review and clarification"],
      ["Employees", "Attendance, leave, documents, onboarding and policies"],
    ],
  },
  {
    department: "Security",
    icon: "shield",
    connectedTo: [
      ["Admin", "Gate logs, guard management and vehicle records"],
      ["Logistics", "Vehicle entry, dispatch movement and challan"],
      ["Purchase", "Purchase order view for material inward"],
      ["Supplier / Vendor", "Gate entry and material inward/outward tracking"],
    ],
  },
];

const roleMovements = [
  ["Admin", "Creates department, role and permissions", "Employee role owner", "Employee gets module access"],
  ["Vendor", "Submits product requirement", "Admin approver", "Requirement approved/rejected or assigned"],
  ["Admin approver", "Approves vendor requirement", "Assigned employee / Dispatch user", "Requirement moves to processing"],
  ["Dispatch user", "Updates tracking, vehicle and delivery status", "Accounts invoice/payment user", "Invoice/payment record is prepared"],
  ["Supplier", "Submits dispatch against PO/material", "Admin / Plant reviewer", "Dispatch enters quality workflow"],
  ["Plant manager", "Assigns supplier dispatch for quality check", "Quality inspector", "Quality inspection starts"],
  ["Quality inspector", "Submits quality report and amount", "Plant manager", "Plant approves, rejects or raises query"],
  ["Plant manager", "Approves supplier quality report", "Accounts payable user", "Payment item becomes ready"],
  ["Contractor", "Submits daily work entry", "Plant contractor approver", "Work is verified at plant"],
  ["Plant contractor approver", "Approves contractor work", "HR + Accounts", "Payroll/payment review starts"],
  ["HR reviewer", "Reviews contractor work or raises query", "Contractor / Plant / Accounts", "Clarification or payroll processing"],
  ["Accounts processor", "Creates payment/payroll/vendor bill update", "Accounts approver", "Approval required"],
  ["Accounts approver", "Approves payment", "Payment release user", "Final release step"],
  ["Sales user", "Creates lead/deal/pricing request", "Pricing approver / Admin", "Pricing approval or sales reporting"],
  ["Purchase requester", "Creates purchase request", "Purchase order creator", "PO creation step"],
  ["Logistics planner", "Creates dispatch request", "Packing / Dispatch manager", "Packing and dispatch execution"],
];

const recommendations = [
  {
    area: "Sales to Accounts",
    priority: "High",
    current: "Sales pricing approval exists, but final deal-to-invoice handoff should be more explicit.",
    recommendation: "After deal is marked won/converted, notify Accounts invoice creator and create an invoice draft task.",
    nextConnection: "Sales Executive -> Sales Manager/Pricing Approver -> Accounts Invoice Creator",
  },
  {
    area: "Sales to Logistics",
    priority: "High",
    current: "Dispatch flows exist, but sales/order conversion should clearly trigger dispatch planning.",
    recommendation: "After customer order/deal confirmation, create a Logistics dispatch request with customer, product and delivery details.",
    nextConnection: "Sales Executive -> Logistics Planner -> Packing/Dispatch Manager",
  },
  {
    area: "Purchase to Inventory",
    priority: "High",
    current: "Purchase requests and inventory GRN exist as separate modules.",
    recommendation: "After PO is created/closed, notify Inventory GRN user so received material can be entered and stock updated.",
    nextConnection: "Purchase Order Creator -> Inventory GRN User -> Stock Ledger Manager",
  },
  {
    area: "Inventory to Quality",
    priority: "High",
    current: "Quality checks are present, supplier dispatch quality is present, but GRN quality gate should be explicit.",
    recommendation: "For incoming raw material, GRN should trigger Quality inspection before stock becomes accepted/available.",
    nextConnection: "Inventory GRN User -> Quality Inspector -> Inventory Stock Manager",
  },
  {
    area: "Quality to Accounts",
    priority: "High",
    current: "Supplier quality approved/agreed amount reaches Accounts for payable.",
    recommendation: "Keep this handoff mandatory: Accounts should only process supplier payment after Quality report and Plant decision are final.",
    nextConnection: "Quality Inspector -> Plant Manager -> Accounts Payable User",
  },
  {
    area: "Plant to Purchase",
    priority: "Medium",
    current: "Plant has production and stock consumption; purchase has request/order modules.",
    recommendation: "When stock consumption or inventory alert crosses reorder level, auto-create or notify Purchase request creator.",
    nextConnection: "Plant Production User / Inventory Alert User -> Purchase Request Creator",
  },
  {
    area: "Plant to Maintenance",
    priority: "Medium",
    current: "Breakdowns and maintenance schedules exist inside Plant.",
    recommendation: "When production report has downtime/breakdown, notify Maintenance approver and create maintenance log follow-up.",
    nextConnection: "Plant Production Reporter -> Maintenance Manager -> Plant Manager",
  },
  {
    area: "Contractor to HR and Accounts",
    priority: "High",
    current: "Plant-approved contractor work is visible to HR and Accounts.",
    recommendation: "HR should validate manpower/work policy first, then Accounts should process payment. If HR raises query, Accounts payment should stay blocked.",
    nextConnection: "Contractor -> Plant Approver -> HR Reviewer -> Accounts Processor",
  },
  {
    area: "Security to Logistics",
    priority: "Medium",
    current: "Security handles vehicle logs and Logistics handles dispatch/tracking.",
    recommendation: "Vehicle gate-in/gate-out should update Logistics tracking automatically or notify Logistics tracking user.",
    nextConnection: "Guard/Security User -> Logistics Tracking User -> Admin Visibility",
  },
  {
    area: "Admin to Department Heads",
    priority: "Medium",
    current: "Admin can manage all records, but department escalation ownership can be clearer.",
    recommendation: "Create department head roles for HR, Accounts, Sales, Plant, Quality, Purchase, Logistics and Security for escalations.",
    nextConnection: "Admin -> Department Head -> Department Team Members",
  },
  {
    area: "Accounts Payment Release",
    priority: "High",
    current: "Payment update, approval and release permissions are separate.",
    recommendation: "Keep maker-checker-release separation: processor creates payment, approver approves, release user completes payment.",
    nextConnection: "Accounts Processor -> Accounts Approver -> Payment Release User",
  },
  {
    area: "Audit and Reports",
    priority: "Medium",
    current: "Finance audit logs and reports exist.",
    recommendation: "Every major approval/rejection/payment should be visible in Admin reports and Finance audit logs with actor role and timestamp.",
    nextConnection: "All Departments -> Audit Log / Admin Reports",
  },
];

const links = [
  ["Vendor", "Admin", "Approval"],
  ["Admin", "Employee", "Assignment"],
  ["Employee", "Logistics", "Dispatch"],
  ["Logistics", "Accounts", "Invoice / Payment"],
  ["Supplier", "Plant", "Dispatch Review"],
  ["Plant", "Quality", "Inspection"],
  ["Quality", "Plant", "Report"],
  ["Plant", "Accounts", "Payable"],
  ["Contractor", "Plant", "Work Approval"],
  ["Plant", "HR", "Payroll Review"],
  ["HR", "Accounts", "Payment Processing"],
];

const guidedFlows = [
  { key: "purchase", title: "Purchase to Supplier", icon: "shopping_cart_checkout", color: "#7c3aed", summary: "Requirement se approved PO aur supplier dispatch tak.", steps: [
    { actor: "Department / Purchase", title: "Purchase Request", action: "Item, quantity, unit, needed date aur specification bhari जाती है.", next: "Pending Request", guard: "Positive quantity aur valid date mandatory." },
    { actor: "Purchase Approver", title: "Request Approval", action: "Requirement check karke approve ya reject करता है.", next: "Approved Request", guard: "Request बनाने वाला खुद approve नहीं कर सकता." },
    { actor: "Purchase Team", title: "Quotation Comparison", action: "Approved suppliers के rates, tax, freight और delivery compare होते हैं.", next: "Final Quotation", guard: "एक request पर केवल एक quotation final." },
    { actor: "PO Creator / Approver", title: "Purchase Order", action: "Final quotation से amount calculate होकर PO बनता और approve होता है.", next: "Supplier Assigned", guard: "Creator self-approve नहीं कर सकता; पुराना open PO block करेगा." },
    { actor: "Supplier", title: "Material Dispatch", action: "Supplier PO की remaining quantity के अंदर vehicle dispatch submit करता है.", next: "Gate Verification", guard: "Remaining PO quantity से अधिक dispatch blocked." },
  ]},
  { key: "supplier", title: "Vehicle to GRN", icon: "local_shipping", color: "#ea580c", summary: "Supplier vehicle gate से quality, unloading और GRN तक.", steps: [
    { actor: "Supplier", title: "Vehicle Leaves", action: "Vehicle, driver, seal, quantity और documents submit होते हैं.", next: "In Transit", guard: "Approved PO और valid quantity required." },
    { actor: "Security Guard", title: "Gate Check", action: "QR/gate pass, vehicle, driver, seal और gross weight verify करता है.", next: "Gate Entered", guard: "Mismatch पर entry रोकी जाती है." },
    { actor: "Quality Inspector", title: "Quality Inspection", action: "Accepted/rejected quantity, rate और deductions दर्ज होते हैं.", next: "Plant Decision", guard: "Quality pass बिना unloading आगे नहीं बढ़ेगी." },
    { actor: "Plant / Warehouse", title: "Unloading & Tare", action: "Unloading photos, empty vehicle और tare weight दर्ज होता है.", next: "GRN Ready", guard: "Tare gross weight से कम होना चाहिए." },
    { actor: "System", title: "GRN Generated", action: "Net quantity, deductions और net payable automatic calculate होता है.", next: "Supplier Agreement", guard: "Calculation audit trail में lock रहती है." },
  ]},
  { key: "accounts", title: "GRN to Payment", icon: "account_balance_wallet", color: "#16a34a", summary: "Supplier agreement से secure payment release तक.", steps: [
    { actor: "Supplier", title: "GRN Agreement", action: "Supplier received quantity और payable amount agree/dispute करता है.", next: "Accounts Ready", guard: "Agreement बिना payment approve नहीं होगा." },
    { actor: "System / Accounts", title: "Vendor Bill", action: "GRN से vendor bill और payable ledger बनता है.", next: "Pending Payment", guard: "Duplicate GRN bill blocked." },
    { actor: "Accounts Maker", title: "Payment Entry", action: "Payment pending state में तैयार होती है.", next: "Pending Approval", guard: "Maker खुद approve नहीं कर सकता." },
    { actor: "Accounts Checker", title: "Payment Approval", action: "Bill, amount और bank details verify करके approve करता है.", next: "Approved Payment", guard: "Approved record edit नहीं होगा." },
    { actor: "Payment Releaser", title: "Payment Release", action: "Mode, UTR/reference और proof के साथ payment release करता है.", next: "Paid & Ledger Posted", guard: "Separate release permission और UTR mandatory." },
  ]},
  { key: "thumb", title: "Government Thumb", icon: "fingerprint", color: "#0f766e", summary: "Security eligibility से product thumb verification और vendor confirmation तक.", steps: [
    { actor: "Admin / Accounts", title: "Amount Allotment", action: "Vendor और Government officer select करके thumb limit allot करता है.", next: "Assigned", guard: "Allotment available security eligibility से अधिक नहीं." },
    { actor: "Government Officer", title: "Product Verification", action: "Product/material name, product quantity, unit, amount और thumb detail submit करता है.", next: "Submitted", guard: "Used amount remaining allotment से अधिक नहीं." },
    { actor: "Vendor", title: "Vendor Review", action: "Vendor entry को Confirm या reason के साथ Dispute करता है.", next: "Confirmed / Disputed", guard: "एक entry का review केवल एक बार." },
    { actor: "System", title: "Balance & Notifications", action: "Confirm history lock करता है; dispute amount remaining में वापस जोड़ता है.", next: "Live Ledger Updated", guard: "Admin, Government और Vendor तीनों को notification." },
  ]},
  { key: "vendor", title: "Vendor Requirement", icon: "storefront", color: "#2563eb", summary: "Vendor requirement से dispatch, invoice और payment तक.", steps: [
    { actor: "Vendor", title: "Requirement", action: "Catalog product, quantity और delivery information submit करता है.", next: "Admin Review", guard: "Profile approval और valid product required." },
    { actor: "Admin / Employee", title: "Approve & Assign", action: "Requirement approve करके responsible employee को assign किया जाता है.", next: "Processing", guard: "Rejected requirement आगे नहीं जाएगी." },
    { actor: "Dispatch Team", title: "Dispatch & Delivery", action: "Vehicle, tracking और delivery proof update होते हैं.", next: "Delivered", guard: "हर status का timestamp/history सुरक्षित." },
    { actor: "Accounts", title: "Invoice & Receipt", action: "Invoice/ledger बनता और vendor payment track होता है.", next: "Closed", guard: "Payment approval और release अलग controls." },
  ]},
  { key: "hr", title: "Employee to Payroll", icon: "badge", color: "#e11d48", summary: "Employee onboarding, attendance, leave और salary release.", steps: [
    { actor: "Admin / HR", title: "Onboarding", action: "Employee profile, department, role और permissions assign होते हैं.", next: "Active Employee", guard: "Role permissions ही module access तय करती हैं." },
    { actor: "Employee / HR", title: "Attendance & Leave", action: "Attendance, worktime और leave requests submit/review होते हैं.", next: "Verified Inputs", guard: "Unapproved attendance payroll input नहीं बनेगी." },
    { actor: "HR / Accounts", title: "Payroll Prepared", action: "Salary breakup और month-wise payroll pending बनता है.", next: "Payroll Approval", guard: "Duplicate employee-month payroll blocked." },
    { actor: "Approver / Releaser", title: "Salary Release", action: "Approval के बाद bank mode/reference से salary release होती है.", next: "Released", guard: "Maker-checker और separate release." },
  ]},
  { key: "sales", title: "Lead to Collection", icon: "trending_up", color: "#4f46e5", summary: "Lead से deal, pricing, delivery और customer collection.", steps: [
    { actor: "Sales", title: "Lead Captured", action: "Manual/Meta lead owner और follow-up के साथ बनती है.", next: "Qualified Lead", guard: "Duplicate/contact validation." },
    { actor: "Sales Manager", title: "Pricing Approval", action: "Rate/discount request review होता है.", next: "Approved Offer", guard: "Unauthorized discount block." },
    { actor: "Sales", title: "Deal Won", action: "Customer order confirm करके dispatch handoff बनता है.", next: "Delivery", guard: "Product और delivery detail mandatory." },
    { actor: "Accounts", title: "Invoice & Collection", action: "Sales invoice और incoming payment ledger update होता है.", next: "Closed Deal", guard: "Posted invoice और released receipt audit locked." },
  ]},
  { key: "contractor", title: "Contractor Work", icon: "engineering", color: "#b45309", summary: "Daily work entry से Plant, HR, Accounts और payment confirmation तक.", steps: [
    { actor: "Contractor", title: "Daily Work Entry", action: "Work type, manpower, quantity, rate और proof submit करता है.", next: "Plant Review", guard: "Configured rate और evidence required." },
    { actor: "Plant Manager", title: "Work Verification", action: "Actual site work approve, reject या clarification में भेजता है.", next: "HR Review", guard: "Rejected/queried work payment तक नहीं जाएगा." },
    { actor: "HR", title: "Policy & Manpower Review", action: "Manpower, attendance और policy compliance verify करता है.", next: "Accounts Ready", guard: "HR query होने पर Accounts block रहेगा." },
    { actor: "Accounts", title: "Payment Processing", action: "Approved work से payable और payment approval बनती है.", next: "Payment Released", guard: "Maker-checker-release separation." },
    { actor: "Contractor", title: "Receipt Confirmation", action: "Contractor received payment confirm करता है.", next: "Work Closed", guard: "Reference और complete audit history सुरक्षित." },
  ]},
];

const AnimatedFlowPlayer = () => {
  const [flowKey, setFlowKey] = useState(guidedFlows[0].key);
  const [stepIndex, setStepIndex] = useState(0);
  const [playing, setPlaying] = useState(true);
  const flow = useMemo(() => guidedFlows.find((item) => item.key === flowKey) || guidedFlows[0], [flowKey]);
  useEffect(() => { setStepIndex(0); }, [flowKey]);
  useEffect(() => {
    if (!playing) return undefined;
    const timer = window.setInterval(() => setStepIndex((value) => (value + 1) % flow.steps.length), 2400);
    return () => window.clearInterval(timer);
  }, [playing, flow.steps.length]);
  const active = flow.steps[stepIndex];
  return <section className="awm-player" style={{ "--flow-color": flow.color }}>
    <div className="awm-player-tabs">{guidedFlows.map((item) => <button key={item.key} className={item.key === flowKey ? "active" : ""} onClick={() => setFlowKey(item.key)}><span className="material-symbols-outlined">{item.icon}</span>{item.title}</button>)}</div>
    <div className="awm-player-head"><div><span className="awm-live-dot"/> Animated flow guide<h2>{flow.title}</h2><p>{flow.summary}</p></div><button className="awm-play" onClick={() => setPlaying((v) => !v)}><span className="material-symbols-outlined">{playing ? "pause" : "play_arrow"}</span>{playing ? "Pause" : "Play"}</button></div>
    <div className="awm-animated-track">{flow.steps.map((step, index) => <React.Fragment key={step.title}><button className={`awm-animated-step${index === stepIndex ? " active" : ""}${index < stepIndex ? " done" : ""}`} onClick={() => { setStepIndex(index); setPlaying(false); }}><span>{index < stepIndex ? "✓" : index + 1}</span><small>{step.actor}</small><strong>{step.title}</strong></button>{index < flow.steps.length - 1 ? <div className={`awm-moving-line${index < stepIndex ? " done" : ""}`}><i/></div> : null}</React.Fragment>)}</div>
    <div className="awm-active-explain"><div><span>कौन करेगा</span><strong>{active.actor}</strong></div><div><span>क्या होगा</span><strong>{active.action}</strong></div><div><span>अगला status</span><strong>{active.next}</strong></div><div className="guard"><span>Safety check</span><strong>{active.guard}</strong></div></div>
  </section>;
};

const FlowSteps = ({ steps }) => (
  <div className="awm-step-line">
    {steps.map((step, index) => (
      <React.Fragment key={step}>
        <div className="awm-step">
          <span>{index + 1}</span>
          <strong>{step}</strong>
        </div>
        {index < steps.length - 1 ? (
          <span className="material-symbols-outlined awm-step-arrow">arrow_forward</span>
        ) : null}
      </React.Fragment>
    ))}
  </div>
);

export default function AdminWorkflowMap() {
  return (
    <Container fluid className="awm-page">
      <header className="awm-header">
        <div>
          <Badge bg="light" text="dark" className="awm-eyebrow">Admin workflow map</Badge>
          <h1>Department Connections & User Movement</h1>
          <p>
            Visual overview of how external users, internal departments, permissions and handoff notifications are connected.
          </p>
        </div>
        <div className="awm-header-stat">
          <strong>{departments.length}</strong>
          <span>Connected departments</span>
        </div>
      </header>

      <AnimatedFlowPlayer />

      <section className="awm-band">
        <div className="awm-section-head">
          <h2>Department Map</h2>
          <p>Admin assigns every employee to a department role. Role permissions decide which module and next workflow action the user can handle.</p>
        </div>
        <div className="awm-dept-grid">
          {departments.map((dept) => (
            <article key={dept.key} className={`awm-dept awm-${dept.tone}`}>
              <div className="awm-dept-top">
                <span className="material-symbols-outlined">{dept.icon}</span>
                <h3>{dept.title}</h3>
              </div>
              <p>{dept.summary}</p>
              <div className="awm-chip-row">
                {dept.items.map((item) => <span key={item}>{item}</span>)}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="awm-band">
        <div className="awm-section-head">
          <h2>Main Connection Diagram</h2>
          <p>These are the core project handoffs: who starts the work, where it moves, and why the next department receives it.</p>
        </div>
        <div className="awm-network">
          {links.map(([from, to, label]) => (
            <div key={`${from}-${to}-${label}`} className="awm-link-row">
              <div className="awm-node">{from}</div>
              <div className="awm-connector">
                <span>{label}</span>
              </div>
              <div className="awm-node">{to}</div>
            </div>
          ))}
        </div>
      </section>

      <section className="awm-band">
        <div className="awm-section-head">
          <h2>Department to Department Connections</h2>
          <p>Every department can see which teams it depends on and what information or task moves between them.</p>
        </div>
        <div className="awm-connection-grid">
          {departmentConnections.map((row) => (
            <article key={row.department} className="awm-connection-card">
              <div className="awm-connection-title">
                <span className="material-symbols-outlined">{row.icon}</span>
                <h3>{row.department}</h3>
              </div>
              <div className="awm-connection-list">
                {row.connectedTo.map(([name, reason]) => (
                  <div key={`${row.department}-${name}`} className="awm-connection-item">
                    <strong>{name}</strong>
                    <span>{reason}</span>
                  </div>
                ))}
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="awm-band">
        <div className="awm-section-head">
          <h2>External User Flows</h2>
          <p>Vendor, supplier and contractor records enter the company through Admin, then move through operational departments.</p>
        </div>
        <div className="awm-flow-list">
          {externalFlows.map((flow) => (
            <article key={flow.title} className="awm-flow">
              <div className="awm-flow-title">
                <span>{flow.actor}</span>
                <h3>{flow.title}</h3>
              </div>
              <FlowSteps steps={flow.steps} />
            </article>
          ))}
        </div>
      </section>

      <section className="awm-band">
        <div className="awm-section-head">
          <h2>Role Work Movement</h2>
          <p>This shows each role's work and the next role/department that receives the task after completion.</p>
        </div>
        <div className="awm-table-wrap">
          <Table responsive className="awm-table">
            <thead>
              <tr>
                <th>Role / User</th>
                <th>What They Do</th>
                <th>Next Role / Department</th>
                <th>What Happens Next</th>
              </tr>
            </thead>
            <tbody>
              {roleMovements.map(([role, work, nextRole, result]) => (
                <tr key={`${role}-${nextRole}-${work}`}>
                  <td><strong>{role}</strong></td>
                  <td>{work}</td>
                  <td>{nextRole}</td>
                  <td>{result}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </section>

      <section className="awm-band">
        <div className="awm-section-head">
          <h2>Recommended Role Connections</h2>
          <p>Suggested next-step connections to make department-to-department movement clearer and reduce manual follow-up.</p>
        </div>
        <div className="awm-recommendation-grid">
          {recommendations.map((item) => (
            <article key={`${item.area}-${item.nextConnection}`} className="awm-recommendation-card">
              <div className="awm-recommendation-top">
                <h3>{item.area}</h3>
                <span className={`awm-priority awm-priority-${item.priority.toLowerCase()}`}>{item.priority}</span>
              </div>
              <div className="awm-recommendation-block">
                <strong>Current</strong>
                <p>{item.current}</p>
              </div>
              <div className="awm-recommendation-block">
                <strong>Recommendation</strong>
                <p>{item.recommendation}</p>
              </div>
              <div className="awm-next-connection">
                <span className="material-symbols-outlined">route</span>
                <span>{item.nextConnection}</span>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="awm-band">
        <div className="awm-section-head">
          <h2>Permission-Based Handoff Table</h2>
          <p>The next user is selected by role permission, so replacing a person does not break the workflow.</p>
        </div>
        <div className="awm-table-wrap">
          <Table responsive className="awm-table">
            <thead>
              <tr>
                <th>Action</th>
                <th>From</th>
                <th>Next User / Department</th>
                <th>Required Permission</th>
              </tr>
            </thead>
            <tbody>
              {handoffs.map(([action, from, next, permission]) => (
                <tr key={action}>
                  <td><strong>{action}</strong></td>
                  <td>{from}</td>
                  <td>{next}</td>
                  <td><code>{permission}</code></td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      </section>
    </Container>
  );
}
